/**
 * JourneyPage — Unified subject detail view (DRR-189).
 *
 * The ONE page a surveyor, DoN, or RA opens for any subject (resident/client/matter).
 * Five tabs surface the three FERP hierarchies:
 *   - Tasks      → Work hierarchy (pending + completed tasks for this subject)
 *   - Events     → Instance lifecycle (chronological workflow events)
 *   - Team       → People hierarchy (constellation Ring 1-2 care team)
 *   - Clinical   → FHIR subgraph (conditions, meds, observations — if enabled)
 *   - Graph      → Neural coordination graph (multi-actor intersection)
 *
 * Route: /ferp-core/journey?subject={id}&tab={tasks|events|team|clinical|graph}
 * Layer: user (all staff — RA, DoN, ALD, architect)
 * Scope: task:read (DRR-102)
 *
 * DRR-055: Dumb frontend — all labels from settingBundle.subjectLabel.
 * DRR-134: FocusContext auto-selects subject.
 * DRR-114: T2 operational data only. No duration variance on this page.
 * DRR-123: Clinical tab only appears if FHIR subgraph is enabled.
 */

import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Alert,
  Badge,
  Box,
  Card,
  Group,
  Select,
  Skeleton,
  Stack,
  Tabs,
  Text,
  Title,
} from '@mantine/core';
import {
  IconAlertCircle,
  IconChecklist,
  IconTimeline,
  IconUsers,
  IconStethoscope,
  IconShare,
} from '@tabler/icons-react';
import {
  JourneyTimeline,
  NeuralJourneyGraph,
  useSubjectJourneyTimeline,
  useEntityJourneyGraph,
  type JourneyTimelineEvent,
} from '@ferp/workflow-forms';
import {
  GqlActorType,
  GqlTaskStatus,
  useGetActorsByTypeQuery,
  useGetTasksBySubjectQuery,
  useConstellationsByActorQuery,
} from '@/generated/graphql';
import { useNavigation } from '@/contexts/NavigationContext';
import { useFocusContext } from '@/contexts/FocusContext';
import { useAuth } from '@/auth';
import { FhirClinicalPanel } from '@ferp/subgraph-fhir';

// ---------------------------------------------------------------------------
// Tasks sub-view — grouped by status (DRR-189 WS-1)
// ---------------------------------------------------------------------------
function TasksView({ subjectActorId }: { readonly subjectActorId: string }) {
  const { data, loading } = useGetTasksBySubjectQuery({
    variables: { subjectActorId },
    skip: !subjectActorId,
    fetchPolicy: 'cache-and-network',
  });

  const tasks = data?.taskApiGetTasksBySubject ?? [];

  const activeTasks = useMemo(
    () =>
      tasks.filter(
        (t) =>
          t.status === GqlTaskStatus.PENDING ||
          t.status === GqlTaskStatus.ASSIGNED ||
          t.status === GqlTaskStatus.IN_PROGRESS,
      ),
    [tasks],
  );

  const completedTasks = useMemo(
    () => tasks.filter((t) => t.status === GqlTaskStatus.COMPLETED),
    [tasks],
  );

  if (loading && tasks.length === 0) {
    return (
      <Stack gap="sm">
        <Skeleton height={60} />
        <Skeleton height={60} />
        <Skeleton height={60} />
      </Stack>
    );
  }

  if (tasks.length === 0) {
    return (
      <Alert icon={<IconChecklist size={16} />} color="blue" variant="light">
        No tasks found for this subject.
      </Alert>
    );
  }

  return (
    <Stack gap="md">
      {activeTasks.length > 0 && (
        <>
          <Group gap="xs">
            <Text fw={600} size="sm">Active Now</Text>
            <Badge size="sm" color="orange">{activeTasks.length}</Badge>
          </Group>
          {activeTasks.map((task) => (
            <Card key={task.id} withBorder padding="sm" radius="sm">
              <Group justify="space-between" wrap="nowrap">
                <Box>
                  <Text fw={500} size="sm" lineClamp={1}>{task.name}</Text>
                  <Group gap="xs" mt={4}>
                    <Badge size="xs" color={task.status === GqlTaskStatus.IN_PROGRESS ? 'green' : 'yellow'}>
                      {task.status}
                    </Badge>
                    {task.priority && (
                      <Badge size="xs" variant="outline" color={task.priority === 'CRITICAL' ? 'red' : 'gray'}>
                        {task.priority}
                      </Badge>
                    )}
                    {task.assignedActor?.displayName && (
                      <Text size="xs" c="dimmed">
                        Assigned: {task.assignedActor.displayName}
                      </Text>
                    )}
                  </Group>
                </Box>
                {task.mandateRef && (
                  <Badge size="xs" variant="light" color="violet">
                    Regulated
                  </Badge>
                )}
              </Group>
            </Card>
          ))}
        </>
      )}

      {completedTasks.length > 0 && (
        <>
          <Group gap="xs" mt={activeTasks.length > 0 ? 'md' : 0}>
            <Text fw={600} size="sm">Completed</Text>
            <Badge size="sm" color="green">{completedTasks.length}</Badge>
          </Group>
          {completedTasks.map((task) => (
            <Card key={task.id} withBorder padding="sm" radius="sm" style={{ opacity: 0.8 }}>
              <Group justify="space-between" wrap="nowrap">
                <Box>
                  <Text fw={500} size="sm" lineClamp={1}>{task.name}</Text>
                  <Group gap="xs" mt={4}>
                    <Badge size="xs" color="green">{task.status}</Badge>
                    {task.assignedActor?.displayName && (
                      <Text size="xs" c="dimmed">
                        By: {task.assignedActor.displayName}
                      </Text>
                    )}
                    {task.completedAt && (
                      <Text size="xs" c="dimmed">
                        {new Date(task.completedAt).toLocaleDateString()}
                      </Text>
                    )}
                  </Group>
                </Box>
                {task.mandateRef && (
                  <Badge size="xs" variant="light" color="violet">
                    Regulated
                  </Badge>
                )}
              </Group>
            </Card>
          ))}
        </>
      )}
    </Stack>
  );
}

// ---------------------------------------------------------------------------
// Team sub-view — constellation Ring 1-2 members (DRR-189 WS-1)
// ---------------------------------------------------------------------------
function TeamView({ subjectActorId }: { readonly subjectActorId: string }) {
  const { data, loading } = useConstellationsByActorQuery({
    variables: { actorId: subjectActorId },
    skip: !subjectActorId,
    fetchPolicy: 'cache-and-network',
  });

  const memberships = data?.constellationsByActor?.memberships ?? [];

  if (loading && memberships.length === 0) {
    return <Skeleton height={100} />;
  }

  if (memberships.length === 0) {
    return (
      <Alert icon={<IconUsers size={16} />} color="blue" variant="light">
        No care team assigned yet.
      </Alert>
    );
  }

  return (
    <Stack gap="sm">
      {memberships.map((m, idx) => (
        <Card key={`${m.constellationId}-${idx}`} withBorder padding="sm" radius="sm">
          <Group justify="space-between">
            <Box>
              <Text fw={500} size="sm">{m.constellationId}</Text>
              <Text size="xs" c="dimmed">
                Ring {m.ring} &middot; Role: {m.role || 'Member'} &middot; {m.edgeType}
              </Text>
            </Box>
            <Badge size="xs" variant="light">
              Ring {m.ring}
            </Badge>
          </Group>
        </Card>
      ))}
    </Stack>
  );
}

// ---------------------------------------------------------------------------
// Timeline sub-view (existing, refactored)
// ---------------------------------------------------------------------------
function TimelineView({
  subjectActorId,
  onEventClick,
}: {
  readonly subjectActorId: string;
  readonly onEventClick: (event: JourneyTimelineEvent) => void;
}) {
  const journey = useSubjectJourneyTimeline(subjectActorId || undefined, 100);

  return (
    <JourneyTimeline
      events={journey.events}
      activeEvents={journey.activeEvents}
      historicalEvents={journey.historicalEvents}
      totalEvents={journey.totalEvents}
      activeEventCount={journey.activeEventCount}
      loading={journey.loading}
      onEventClick={onEventClick}
    />
  );
}

// ---------------------------------------------------------------------------
// Neural graph sub-view (existing, refactored)
// ---------------------------------------------------------------------------
function GraphView({ actorIds }: { actorIds: string[] }) {
  const graph = useEntityJourneyGraph(actorIds, 200);

  return (
    <NeuralJourneyGraph
      actorNames={graph.actorNames}
      nodes={graph.nodes}
      edges={graph.edges}
      intersectionInstanceIds={graph.intersectionInstanceIds}
      loading={graph.loading}
    />
  );
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

type JourneyTab = 'tasks' | 'events' | 'team' | 'clinical' | 'graph';

export function JourneyPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { focusedSubject } = useFocusContext();
  const { tenant } = useAuth();

  // DRR-313: Subject label from shared NavigationContext
  const { settingBundle } = useNavigation();
  const rawSubjectLabel = settingBundle?.subjectLabel;
  const subjectLabel = rawSubjectLabel && rawSubjectLabel.length > 0 ? rawSubjectLabel : 'Subject';

  // Tab from URL or default to 'tasks' (DRR-189: tasks first for daily work)
  const urlTab = (searchParams.get('tab') as JourneyTab) || 'tasks';
  const [activeTab, setActiveTab] = useState<JourneyTab>(urlTab);

  const handleTabChange = useCallback(
    (tab: string | null) => {
      if (tab) {
        setActiveTab(tab as JourneyTab);
        setSearchParams((prev) => {
          const next = new URLSearchParams(prev);
          next.set('tab', tab);
          return next;
        }, { replace: true });
      }
    },
    [setSearchParams],
  );

  // Priority: ?subject= URL param > FocusContext > manual selection
  const urlSubject = searchParams.get('subject') ?? '';
  const [selectedSubject, setSelectedSubject] = useState<string>(urlSubject);

  // Sync from FocusContext when no URL param is present
  useEffect(() => {
    if (!urlSubject && focusedSubject && !selectedSubject) {
      setSelectedSubject(focusedSubject);
    }
  }, [urlSubject, focusedSubject, selectedSubject]);

  const handleEventClick = useCallback(
    (event: JourneyTimelineEvent) => {
      navigate(`/ferp-core/instances?instance=${encodeURIComponent(event.instanceId)}`);
    },
    [navigate],
  );

  // Fetch SUBJECT actors from the backend — dumb frontend (DRR-055)
  const { data: actorsData, loading: actorsLoading, error: actorsError } = useGetActorsByTypeQuery({
    variables: { actorType: GqlActorType.SUBJECT },
  });

  const subjectOptions = useMemo(
    () =>
      (actorsData?.getActorsByType ?? []).map((a) => ({
        value: a.id,
        label: a.name,
      })),
    [actorsData],
  );

  // Auto-select first subject once loaded
  const effectiveSubject = selectedSubject || (subjectOptions[0]?.value ?? '');

  // Resolve authUserId for the selected subject — FHIR data uses Patient/{authUserId} references
  const effectiveSubjectAuthUserId = useMemo(() => {
    const actor = (actorsData?.getActorsByType ?? []).find((a) => a.id === effectiveSubject);
    return actor?.authUserId ?? effectiveSubject;
  }, [actorsData, effectiveSubject]);

  const graphActorIds = useMemo(
    () => (effectiveSubject ? [effectiveSubject] : []),
    [effectiveSubject],
  );

  // Count active tasks for badge (lightweight — reuses Tasks tab query)
  const { data: tasksData } = useGetTasksBySubjectQuery({
    variables: { subjectActorId: effectiveSubject },
    skip: !effectiveSubject,
    fetchPolicy: 'cache-first',
  });
  const activeTaskCount = useMemo(() => {
    const tasks = tasksData?.getTasksBySubject ?? [];
    return tasks.filter(
      (t) =>
        t.status === GqlTaskStatus.PENDING ||
        t.status === GqlTaskStatus.ASSIGNED ||
        t.status === GqlTaskStatus.IN_PROGRESS,
    ).length;
  }, [tasksData]);

  // Check if FHIR subgraph is available (clinical tab visibility)
  const hasFhir = Boolean(tenant?.num);

  return (
    <Stack gap="lg" p="md">
      <Group justify="space-between" align="flex-end">
        <Box>
          <Title order={2}>{subjectLabel} Journey</Title>
          <Text size="sm" c="dimmed">
            Chronological workflow history and actor coordination graph
          </Text>
        </Box>
      </Group>

      {actorsError && (
        <Alert icon={<IconAlertCircle size={16} />} color="red" variant="light">
          Could not load {subjectLabel.toLowerCase()}s. Please refresh.
        </Alert>
      )}

      {actorsLoading ? (
        <Skeleton height={36} w={280} />
      ) : (
        <Select
          label={subjectLabel}
          data={subjectOptions}
          value={effectiveSubject}
          onChange={(v) => v && setSelectedSubject(v)}
          w={280}
          placeholder={`Select a ${subjectLabel.toLowerCase()}`}
          searchable
        />
      )}

      <Tabs value={activeTab} onChange={handleTabChange}>
        <Tabs.List>
          <Tabs.Tab value="tasks" leftSection={<IconChecklist size={14} />}>
            Tasks
            {activeTaskCount > 0 && (
              <Badge size="xs" ml={6} color="orange" variant="filled">
                {activeTaskCount}
              </Badge>
            )}
          </Tabs.Tab>
          <Tabs.Tab value="events" leftSection={<IconTimeline size={14} />}>
            Care Events
          </Tabs.Tab>
          <Tabs.Tab value="team" leftSection={<IconUsers size={14} />}>
            Team
          </Tabs.Tab>
          {hasFhir && (
            <Tabs.Tab value="clinical" leftSection={<IconStethoscope size={14} />}>
              Clinical
            </Tabs.Tab>
          )}
          <Tabs.Tab value="graph" leftSection={<IconShare size={14} />}>
            Coordination
          </Tabs.Tab>
        </Tabs.List>

        <Tabs.Panel value="tasks" pt="md">
          {effectiveSubject && <TasksView subjectActorId={effectiveSubject} />}
        </Tabs.Panel>

        <Tabs.Panel value="events" pt="md">
          {effectiveSubject && (
            <TimelineView subjectActorId={effectiveSubject} onEventClick={handleEventClick} />
          )}
        </Tabs.Panel>

        <Tabs.Panel value="team" pt="md">
          {effectiveSubject && <TeamView subjectActorId={effectiveSubject} />}
        </Tabs.Panel>

        {hasFhir && (
          <Tabs.Panel value="clinical" pt="md">
            {effectiveSubject && tenant?.num ? (
              <FhirClinicalPanel subjectActorId={effectiveSubjectAuthUserId} tenantNum={tenant.num} />
            ) : (
              <Alert icon={<IconStethoscope size={16} />} color="blue" variant="light">
                No clinical records — clinical data entry pending.
              </Alert>
            )}
          </Tabs.Panel>
        )}

        <Tabs.Panel value="graph" pt="md">
          <GraphView actorIds={graphActorIds} />
        </Tabs.Panel>
      </Tabs>
    </Stack>
  );
}
